import React from 'react';
import { Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const getStatusConfig = (status, theme) => {
  if (status === null || status === undefined) return { color: 'default', label: '—' };
  // Normalize booleans before string operations
  if (typeof status === 'boolean') status = status ? 'active' : 'inactive';
  const normStatus = String(status).toLowerCase().trim();
  const isLight = theme.palette.mode === 'light';

  const configs = {
    // Success states
    active: { color: 'success', label: 'Active' },
    paid: { color: 'success', label: 'Paid' },
    completed: { color: 'success', label: 'Completed' },
    approved: { color: 'success', label: 'Approved' },
    processed: { color: 'success', label: 'Processed' },
    delivered: { color: 'success', label: 'Delivered' },
    refunded: { color: 'success', label: 'Refunded' },
    verified: { color: 'success', label: 'Verified' },
    published: { color: 'success', label: 'Published' },
    resolved: { color: 'success', label: 'Resolved' },
    accepted: { color: 'success', label: 'Accepted' },
    converted: { color: 'success', label: 'Converted' },
    invoiced: { color: 'success', label: 'Invoiced' },

    // Warning states
    pending: { color: 'warning', label: 'Pending' },
    processing: { color: 'warning', label: 'Processing' },
    pending_post: { color: 'info', label: 'Posting...' },
    pending_approval: { color: 'warning', label: 'Awaiting Approval' },
    draft: { color: 'warning', label: 'Draft' },
    on_hold: { color: 'warning', label: 'On Hold' },
    partial: { color: 'warning', label: 'Partial' },
    awaiting_payment: { color: 'warning', label: 'Awaiting Payment' },
    pending_payment: { color: 'warning', label: 'Pending Payment' },
    awaiting_approval: { color: 'warning', label: 'Awaiting Approval' },
    in_progress: { color: 'warning', label: 'In Progress' },
    scheduled: { color: 'warning', label: 'Scheduled' },
    paused: { color: 'warning', label: 'Paused' },

    // Error states
    inactive: { color: 'error', label: 'Inactive' },
    cancelled: { color: 'error', label: 'Cancelled' },
    reversed: { color: 'default', label: 'Reversed' },
    unpaid: { color: 'error', label: 'Unpaid' },
    rejected: { color: 'error', label: 'Rejected' },
    void: { color: 'error', label: 'Void' },
    overdue: { color: 'error', label: 'Overdue' },
    failed: { color: 'error', label: 'Failed' },
    expired: { color: 'error', label: 'Expired' },
    blocked: { color: 'error', label: 'Blocked' },
    suspended: { color: 'error', label: 'Suspended' },
    declined: { color: 'error', label: 'Declined' },

    // Info states
    partially_billed: { color: 'info', label: 'Partially Billed' },
    billed: { color: 'success', label: 'Fully Billed' },
    partially_received: { color: 'warning', label: 'Partially Received' },
    received: { color: 'success', label: 'Fully Received' },
    partially_ordered: { color: 'info', label: 'Partially Ordered' },
    ordered: { color: 'success', label: 'Ordered' },
    partially_paid: { color: 'warning', label: 'Partially Paid' },
    posted: { color: 'success', label: 'Posted' },
    open: { color: 'primary', label: 'Open' },
    archived: { color: 'default', label: 'Archived' },
    sent: { color: 'info', label: 'Sent' },
    review: { color: 'info', label: 'In Review' },
    draft_saved: { color: 'default', label: 'Draft Saved' },
    queued: { color: 'info', label: 'Queued' },
    syncing: { color: 'info', label: 'Syncing' },
    imported: { color: 'info', label: 'Imported' },
    exported: { color: 'info', label: 'Exported' },
  };

  return configs[normStatus] || { color: 'default', label: status };
};

const StatusBadge = ({ status, size = 'small', variant = 'filled', isLoading = false, sx = {}, label: labelProp, ...props }) => {
  const theme = useTheme();
  const config = getStatusConfig(status, theme);
  const label = labelProp !== undefined ? labelProp : (isLoading ? `${config.label}...` : config.label);

  return (
    <Chip
      label={label}
      color={config.color}
      size={size}
      variant={variant}
      sx={{
        fontWeight: 600,
        textTransform: 'capitalize',
        ...(isLoading && {
          animation: 'pulse 1.5s infinite ease-in-out',
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.6 },
            '100%': { opacity: 1 },
          }
        }),
        ...sx
      }}
      {...props}
    />
  );
};

export default StatusBadge;
